import { Typography } from "@/components/common/Typography/Typography"
import { FeedbackSubmissionModal } from "@/components/feature/Modals/FeedbackSubmissionModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { HELP_BASE } from "@/features/helpdesk"
import { INITIAL_ONBOARDING_TOUR, useOnboarding } from "@/features/onboarding"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useLayoutStore } from "@/store/layoutStore"
import { URLS } from "@/utils/urls.const"
import { IconHelpCircle, IconInfoCircle } from "@tabler/icons-react"
import {
  CircleHelp,
  Info,
  LucideExternalLink,
  MailIcon,
  MessageCircle,
} from "lucide-react"
import * as React from "react"
import { useNavigate } from "react-router"

interface InfoCircleMenuProps {
  /**
   * "floating" reproduces today's fixed bottom-right trigger (old sidebar /
   * SIDEBAR_V2 off). "sidebar" renders a small right-aligned trigger meant
   * to live inside the V2 sidebar footer.
   */
  variant: "floating" | "sidebar"
}

function InfoBoxLinksPopover({
  setIsOpen,
  onGuideClick,
  onFeedbackClick,
  onHelpCenterClick,
  variant,
  hideOnboarding,
  onboardingTourEnabled,
  helpCenterEnabled,
}: {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  onGuideClick: () => void
  onFeedbackClick: () => void
  onHelpCenterClick: () => void
  variant: "floating" | "sidebar"
  hideOnboarding: boolean
  onboardingTourEnabled: boolean
  helpCenterEnabled: boolean
}) {
  const { translate } = useTranslation()
  return (
    // Styled and placed like the MUI Popover this replaced: a black elevation-8
    // paper held 16px inside the viewport's bottom-right corner, over the icon.
    <PopoverContent
      side="top"
      align="end"
      sideOffset={-16}
      alignOffset={8}
      collisionPadding={16}
      className="max-h-[calc(100vh-32px)] w-auto max-w-[calc(100vw-32px)] overflow-x-hidden overflow-y-auto rounded border-0 bg-black p-0 shadow-[0px_5px_5px_-3px_rgba(0,0,0,0.2),0px_8px_10px_1px_rgba(0,0,0,0.14),0px_3px_14px_2px_rgba(0,0,0,0.12)]"
    >
      <div className="rounded-none bg-grey3 p-2">
        {helpCenterEnabled ? (
          <button
            onClick={() => {
              setIsOpen(false)
              onHelpCenterClick()
            }}
            className="w-full"
          >
            <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
              <CircleHelp className="h-4 w-4" color="white" />
              <Typography variant="textM" className="rounded-sm p-2">
                {translate("SIDEBAR/HELPDESK")}
              </Typography>
            </div>
          </button>
        ) : null}
        {!onboardingTourEnabled ||
        (variant === "sidebar" && hideOnboarding) ? null : (
          <button
            onClick={() => {
              setIsOpen(false)
              onGuideClick()
            }}
            className="w-full"
          >
            <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
              <IconHelpCircle className="h-4 w-4" color="white" />
              <Typography variant="textM" className="rounded-sm p-2">
                {translate("TOUR/RESTART/LABEL")}
              </Typography>
            </div>
          </button>
        )}
        <button
          onClick={() => {
            setIsOpen(false)
            onFeedbackClick()
          }}
          className="w-full"
        >
          <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
            <MessageCircle className="h-4 w-4" color="white" />
            <Typography variant="textM" className="rounded-sm p-2">
              {translate("SETTINGS/FEEDBACK")}
            </Typography>
          </div>
        </button>
        {helpCenterEnabled ? null : (
          <a
            href={URLS.INFO_BOX.HELP_FAQ}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
              <LucideExternalLink className="h-4 w-4" color="white" />
              <Typography variant="textM" className="rounded-sm p-2">
                {translate("INFO_BOX_LINKS/HELP&FAQ")}
              </Typography>
            </div>
          </a>
        )}
        <a href={URLS.INFO_BOX.AGBS} target="_blank" rel="noopener noreferrer">
          <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
            <LucideExternalLink className="h-4 w-4" color="white" />
            <Typography variant="textM" className="rounded-sm p-2">
              {translate("INFO_BOX_LINKS/AGBS")}
            </Typography>
          </div>
        </a>
        <a
          href={URLS.INFO_BOX.DATA_POLICY}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
            <LucideExternalLink className="h-4 w-4" color="white" />
            <Typography variant="textM" className="rounded-sm p-2">
              {translate("INFO_BOX_LINKS/DATA_POLICY")}
            </Typography>
          </div>
        </a>
        <a href={URLS.INFO_BOX.HELP}>
          <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
            <MailIcon className="h-4 w-4" color="white" />
            <Typography variant="textM" className="rounded-sm p-2">
              {translate("INFO_BOX_LINKS/HELP")}
            </Typography>
          </div>
        </a>
        {variant === "floating" ? (
          <a
            href={URLS.INFO_BOX.FEEDBACK}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="flex flex-row items-center gap-1 px-1.5 hover:cursor-pointer hover:bg-grey2">
              <MessageCircle className="h-4 w-4" color="white" />
              <Typography variant="textM" className="rounded-sm p-2">
                {translate("INFO_BOX_LINKS/HELP_FEEDBACK")}
              </Typography>
            </div>
          </a>
        ) : null}
      </div>
    </PopoverContent>
  )
}

export const InfoCircleMenu: React.FC<InfoCircleMenuProps> = ({ variant }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = React.useState(false)
  const { translate } = useTranslation()
  const { triggerWelcomeModal } = useOnboarding()
  const navigate = useNavigate()
  const isMaterialComplianceEnabled = useFeatureFlag("MATERIAL_COMPLIANCE")
  const helpCenterEnabled = useFeatureFlag("HELPDESK")
  const onboardingTourEnabled = useFeatureFlag("ONBOARDING_TOUR")
  const chatPanelMode = useLayoutStore((state) => state.chatPanelMode)
  const [chatPanelWidth, setChatPanelWidth] = React.useState(0)

  React.useEffect(() => {
    if (variant !== "floating") {
      return
    }
    if (chatPanelMode !== "docked") {
      setChatPanelWidth(0)
      return
    }
    const panel = document.querySelector("[data-chat-panel]")
    if (!(panel instanceof HTMLElement)) {
      setChatPanelWidth(0)
      return
    }
    const update = () => setChatPanelWidth(panel.getBoundingClientRect().width)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(panel)
    return () => observer.disconnect()
  }, [variant, chatPanelMode])

  const handleHelpCenterClick = () => navigate(HELP_BASE)

  const handleGuideClick = () => {
    sessionStorage.removeItem(INITIAL_ONBOARDING_TOUR.storageKey)
    navigate("/")
    setTimeout(() => triggerWelcomeModal(), 100)
  }

  return (
    <>
      {variant === "floating" ? (
        <Popover open={isOpen} onOpenChange={setIsOpen} modal>
          <PopoverTrigger asChild>
            {React.createElement(
              helpCenterEnabled ? IconHelpCircle : IconInfoCircle,
              {
                className:
                  "fixed bottom-2 z-30 cursor-pointer opacity-40 transition-opacity hover:opacity-100",
                style: { right: chatPanelWidth + 8 },
                id: "info-circle",
              },
            )}
          </PopoverTrigger>
          <InfoBoxLinksPopover
            setIsOpen={setIsOpen}
            onGuideClick={handleGuideClick}
            onFeedbackClick={() => setIsFeedbackModalOpen(true)}
            onHelpCenterClick={handleHelpCenterClick}
            variant={variant}
            hideOnboarding={isMaterialComplianceEnabled}
            onboardingTourEnabled={onboardingTourEnabled}
            helpCenterEnabled={helpCenterEnabled}
          />
        </Popover>
      ) : (
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                type="button"
                aria-label={translate("SIDEBAR/INFO")}
              >
                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <Info className="h-5 w-5" aria-hidden="true" />
                </span>
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              {/* The helpdesk has its own entry above this one in the sidebar. */}
              {isMaterialComplianceEnabled || !onboardingTourEnabled ? null : (
                <DropdownMenuItem onSelect={handleGuideClick}>
                  <IconHelpCircle className="h-4 w-4" />
                  {translate("TOUR/RESTART/LABEL")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => setIsFeedbackModalOpen(true)}>
                <MessageCircle className="h-4 w-4" />
                {translate("SETTINGS/FEEDBACK")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {helpCenterEnabled ? null : (
                <DropdownMenuItem asChild>
                  <a
                    href={URLS.INFO_BOX.HELP_FAQ}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LucideExternalLink className="h-4 w-4" />
                    {translate("INFO_BOX_LINKS/HELP&FAQ")}
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <a
                  href={URLS.INFO_BOX.AGBS}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LucideExternalLink className="h-4 w-4" />
                  {translate("INFO_BOX_LINKS/AGBS")}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  href={URLS.INFO_BOX.DATA_POLICY}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <LucideExternalLink className="h-4 w-4" />
                  {translate("INFO_BOX_LINKS/DATA_POLICY")}
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={URLS.INFO_BOX.HELP}>
                  <MailIcon className="h-4 w-4" />
                  {translate("INFO_BOX_LINKS/HELP")}
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      )}
      <FeedbackSubmissionModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
      />
    </>
  )
}
